/**
 * Calendar-day arithmetic on `YYYY-MM-DD` strings.
 *
 * Attendance is recorded per calendar day, never per instant, so every helper here works
 * on date parts and avoids UTC/local conversion. Internally a day is represented as a
 * `Date` pinned to midnight UTC, which makes day arithmetic immune to daylight-saving
 * shifts; only `today()` reads the viewer's local clock.
 */

import type { IsoDate, Weekday } from './types.js';

/** Number of days in a week; used when stepping a calendar grid. */
export const DAYS_PER_WEEK = 7;

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Thrown when a string that must be a `YYYY-MM-DD` calendar day is not one. */
export class InvalidIsoDateError extends Error {
  constructor(public readonly value: string) {
    super(`Not a YYYY-MM-DD calendar day: ${JSON.stringify(value)}`);
    this.name = 'InvalidIsoDateError';
  }
}

/** Returns true when `value` is a syntactically valid and real calendar day. */
export function isIsoDate(value: unknown): value is IsoDate {
  if (typeof value !== 'string') return false;
  const match = ISO_DATE_PATTERN.exec(value);
  if (match === null) return false;
  const utc = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return formatIso(new Date(utc)) === value;
}

/**
 * Parses a calendar day into a `Date` at midnight UTC.
 *
 * @throws InvalidIsoDateError when `value` is not a real `YYYY-MM-DD` day.
 */
export function parseIso(value: IsoDate): Date {
  if (!isIsoDate(value)) throw new InvalidIsoDateError(String(value));
  const match = ISO_DATE_PATTERN.exec(value)!;
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
}

/** Formats a `Date` as the `YYYY-MM-DD` day it falls on in UTC. */
export function formatIso(date: Date): IsoDate {
  const year = String(date.getUTCFullYear()).padStart(4, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Returns the viewer's current calendar day, read from the local clock. */
export function today(now: Date = new Date()): IsoDate {
  const year = String(now.getFullYear()).padStart(4, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Returns the local calendar day an epoch-millisecond timestamp falls on. */
export function dayOf(timestamp: number): IsoDate {
  return today(new Date(timestamp));
}

/** Returns the day `count` days after `date`; a negative `count` moves backwards. */
export function addDays(date: IsoDate, count: number): IsoDate {
  const shifted = parseIso(date);
  shifted.setUTCDate(shifted.getUTCDate() + count);
  return formatIso(shifted);
}

/** Returns the day `count` months after `date`, clamped to the end of the target month. */
export function addMonths(date: IsoDate, count: number): IsoDate {
  const start = parseIso(date);
  const targetMonth = start.getUTCMonth() + count;
  const lastDay = new Date(Date.UTC(start.getUTCFullYear(), targetMonth + 1, 0)).getUTCDate();
  return formatIso(new Date(Date.UTC(
    start.getUTCFullYear(),
    targetMonth,
    Math.min(start.getUTCDate(), lastDay),
  )));
}

/** Returns the weekday of `date`, `0` = Sunday. */
export function weekdayOf(date: IsoDate): Weekday {
  return parseIso(date).getUTCDay() as Weekday;
}

/** Returns the first day of the month containing `date`. */
export function startOfMonth(date: IsoDate): IsoDate {
  const start = parseIso(date);
  return formatIso(new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1)));
}

/** Returns the last day of the month containing `date`. */
export function endOfMonth(date: IsoDate): IsoDate {
  const start = parseIso(date);
  return formatIso(new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 0)));
}

/** Returns the whole number of days from `from` to `to`; negative when `to` precedes `from`. */
export function daysBetween(from: IsoDate, to: IsoDate): number {
  const millisPerDay = 86_400_000;
  return Math.round((parseIso(to).getTime() - parseIso(from).getTime()) / millisPerDay);
}

/** Returns every day from `from` to `to` inclusive, ascending. Empty when `to` precedes `from`. */
export function eachDay(from: IsoDate, to: IsoDate): IsoDate[] {
  const days: IsoDate[] = [];
  const span = daysBetween(from, to);
  for (let offset = 0; offset <= span; offset += 1) days.push(addDays(from, offset));
  return days;
}

/** Returns the earlier of two days. */
export function minDate(a: IsoDate, b: IsoDate): IsoDate {
  return a <= b ? a : b;
}

/** Returns the later of two days. */
export function maxDate(a: IsoDate, b: IsoDate): IsoDate {
  return a >= b ? a : b;
}

/** Returns true when `date` falls within the inclusive `[start, end]` window, `null` meaning open. */
export function withinRange(date: IsoDate, start: IsoDate | null, end: IsoDate | null): boolean {
  if (start !== null && date < start) return false;
  if (end !== null && date > end) return false;
  return true;
}
